import {
  Box,
  styled,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { CustomTableFontCell } from 'components/CustomTableFontCell';
import ProjectTileListView from 'components/ProjectTileListView';
import UseAuthContext from 'context/AuthContext';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { USER_ROLES } from 'utils/constants';

let DEFAULT_ORDER = 'desc',
  DEFAULT_ORDER_BY = 'updatedAt';

export const StyledTableSortLabel = styled(TableSortLabel)(({ theme }) => ({
  "&.Mui-active": {
    color: theme.palette.primary.main,
  },
  "&.MuiTableSortLabel-root": {
    fontWeight: "bold",
    color: "black",
    "&:hover": {
      color: theme.palette.primary.main,
    },
  },
  ".MuiTableSortLabel-icon:hover": {
    color: "black",

    backgroundColor: "#dcdcdc",
    borderRadius: "50%",
    border: "1px solid #D3D3D3",
  },
  ".MuiTableSortLabel-icon": {
    border: "1px solid #D3D3D3",
    color: "black",
    borderRadius: "50%",
  },
  "&.Mui-active .MuiTableSortLabel-icon": {
    border: "none",
  },

  "&.Mui-active, .MuiTableSortLabel-icon:hover": {
    color: theme.palette.primary.main,

    backgroundColor: "none !important",

  }
}));

const ProjectListView = ({
  data,
  pagination,
  page,
  pageSize,
  sortBy,
  sortOrder,
  onSort,
  onPageChange,
  onPageSizeChange,
  refetchingData,
  deleteProjecthandler
}: any) => {
  const { t } = useTranslation();
  const { role } = useContext(UseAuthContext);
  
  const getRevisionsCount = (item: any) => {
    return Array.isArray(item.revisions) ? item.revisions.length : 0;
  };

  const INSPECTION_STATUS_RANK: Record<string, number> = {
    awaiting: 1,
    pending: 2,
    reviewed: 2,
    checked: 3,
  };

  const getInspectionRank = (item: any) => {
    const status = item?.inspection?.inspectorStatus;
    return status ? (INSPECTION_STATUS_RANK[status] ?? 0) : 0;
  };

  const getSortedData = () => {
    if (sortBy === 'revisions') {
      return [...data].sort((a, b) => {
        const countA = getRevisionsCount(a);
        const countB = getRevisionsCount(b);
        return sortOrder === 'asc' ? countA - countB : countB - countA;
      });
    }
    if (sortBy === 'inspection') {
      return [...data].sort((a, b) => {
        const rankA = getInspectionRank(a);
        const rankB = getInspectionRank(b);
        return sortOrder === 'asc' ? rankA - rankB : rankB - rankA;
      });
    }
    return data;
  };

  const sortedData = getSortedData();

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ '&:hover': { boxShadow: 'none' } }}>
            <CustomTableFontCell width="380px" >
              <StyledTableSortLabel
                active={sortBy === "name"}
                direction={sortBy === "name" ? sortOrder : "asc"}
                onClick={() => onSort("name")}
              >
                {t("PROJECT_LIST_TABLE_NAME")}
                {sortBy === "name" ? (
                  <Box component="span" sx={visuallyHidden}>
                    {sortOrder === "desc"
                      ? "sorted descending"
                      : "sorted ascending"}
                  </Box>
                ) : null}
              </StyledTableSortLabel>
            </CustomTableFontCell>
            <CustomTableFontCell
              width="300px"
              sortDirection={sortBy === "client_name" ? sortOrder : false}
            >
              <StyledTableSortLabel
                active={sortBy === "client_name"}
                direction={sortBy === "client_name" ? sortOrder : "asc"}
                onClick={() => onSort("client_name")}
              >
                {t("PROJECT_LIST_TABLE_CLIENT")}
                {sortBy === "client_name" ? (
                  <Box component="span" sx={visuallyHidden}>
                    {sortOrder === "desc"
                      ? "sorted descending"
                      : "sorted ascending"}
                  </Box>
                ) : null}
              </StyledTableSortLabel>
            </CustomTableFontCell>
            <CustomTableFontCell
              width="300px"
              sortDirection={sortBy === "type" ? sortOrder : false}
            >
              <StyledTableSortLabel
                active={sortBy === "type"}
                direction={sortBy === "type" ? sortOrder : "asc"}
                onClick={() => onSort("type")}
              >
                {t("PROJECT_LIST_TABLE_TYPE")}
                {sortBy === "type" ? (
                  <Box component="span" sx={visuallyHidden}>
                    {sortOrder === "desc"
                      ? "sorted descending"
                      : "sorted ascending"}
                  </Box>
                ) : null}
              </StyledTableSortLabel>
            </CustomTableFontCell>
            <CustomTableFontCell
              width="300px"
              sortDirection={sortBy === "climate" ? sortOrder : false}
            >
              <StyledTableSortLabel
                active={sortBy === "climate"}
                direction={sortBy === "climate" ? sortOrder : "asc"}
                onClick={() => onSort("climate")}
              >
                {t("PROJECT_LIST_TABLE_CLIMATE")}
                {sortBy === "climate" ? (
                  <Box component="span" sx={visuallyHidden}>
                    {sortOrder === "desc"
                      ? "sorted descending"
                      : "sorted ascending"}
                  </Box>
                ) : null}
              </StyledTableSortLabel>
            </CustomTableFontCell>
            <CustomTableFontCell
              width="300px"
              sortDirection={sortBy === "revisions" ? sortOrder : false}
            >
              <StyledTableSortLabel
                active={sortBy === "revisions"}
                direction={sortBy === "revisions" ? sortOrder : "asc"}
                onClick={() => onSort("revisions")}
              >
                {t("PROJECT_LIST_TABLE_ALTERNATIVES")}
                {sortBy === "revisions" ? (
                  <Box component="span" sx={visuallyHidden}>
                    {sortOrder === "desc"
                      ? "sorted descending"
                      : "sorted ascending"}
                  </Box>
                ) : null}
              </StyledTableSortLabel>
            </CustomTableFontCell>
           {role === USER_ROLES.ADMIN && <CustomTableFontCell
              width="300px"
              sortDirection={sortBy === "totalRequiredPointsWithTransaction" ? sortOrder : false}
            >
              <StyledTableSortLabel
                active={sortBy === "totalRequiredPointsWithTransaction"}
                direction={sortBy === "totalRequiredPointsWithTransaction" ? sortOrder : "asc"}
                onClick={() => onSort("totalRequiredPointsWithTransaction")}
              >
                {t("PROJECT_LIST_TABLE_TOTAL_REQUIRED_POINTS_WITH_TRANSACTION")}
                {sortBy === "totalRequiredPointsWithTransaction" ? (
                  <Box component="span" sx={visuallyHidden}>
                    {sortOrder === "desc"
                      ? "sorted descending"
                      : "sorted ascending"}
                  </Box>
                ) : null}
              </StyledTableSortLabel>
            </CustomTableFontCell>
            }
            <CustomTableFontCell
              width="300px"
              sortDirection={sortBy === "updatedAt" ? sortOrder : false}
            >
              <StyledTableSortLabel
                active={sortBy === "updatedAt"}
                direction={sortBy === "updatedAt" ? sortOrder : "asc"}
                onClick={() => onSort("updatedAt")}
              >
                {t("PROJECT_LIST_TABLE_DATE")}
                {sortBy === "updatedAt" ? (
                  <Box component="span" sx={visuallyHidden}>
                    {sortOrder === "desc"
                      ? "sorted descending"
                      : "sorted ascending"}
                  </Box>
                ) : null}
              </StyledTableSortLabel>
            </CustomTableFontCell>
            <CustomTableFontCell
              width="140px"
              sortDirection={sortBy === "inspection" ? sortOrder : false}
            >
              <StyledTableSortLabel
                active={sortBy === "inspection"}
                direction={sortBy === "inspection" ? sortOrder : "asc"}
                onClick={() => onSort("inspection")}
              >
                {t("PROJECT_LIST_TABLE_INSPECTION")}
                {sortBy === "inspection" ? (
                  <Box component="span" sx={visuallyHidden}>
                    {sortOrder === "desc"
                      ? "sorted descending"
                      : "sorted ascending"}
                  </Box>
                ) : null}
              </StyledTableSortLabel>
            </CustomTableFontCell>
            <CustomTableFontCell width="10px" align="right"></CustomTableFontCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedData.map((item: any) => (
            <ProjectTileListView
              data={{ ...item, handleDeleteProject: deleteProjecthandler }}
              key={item.id}
              refetch={refetchingData}
            />
          ))}
        </TableBody>
      </Table>

      <TablePagination
        component="div"
        count={pagination?.total}
        rowsPerPageOptions={[20]}

        page={page - 1}
        onPageChange={(_, newPage) => onPageChange(newPage + 1)}
        rowsPerPage={pageSize}
        onRowsPerPageChange={(e) => {
          const newPageSize = parseInt(e.target.value, 10);
          onPageSizeChange(newPageSize);
        }}
        labelRowsPerPage={t('MUI_ROWS_PER_PAGE')}
        labelDisplayedRows={({ from, to, count }) =>
          t('MUI_PAGINATION_RANGE', {
            from: from,
            to: Math.min(to, count),
            count
          })}
      />
    </TableContainer>
  );
};

export default ProjectListView;
